import { apiClient } from "@/services/apiClient";

/**
 * Admin payment actions taken from a request's detail page.
 *
 * Distinct from platformBankTransfersService, which works a queue of transfer
 * receipts. These act on the checkout behind one request, from the page an
 * admin is already on when they spot the problem.
 *
 * Both route by the checkout ROW's provider on the server, so a dLocal-era
 * payment is still asked of dLocal after production moved to Mercado Pago.
 */

/**
 * Ask the provider what actually happened to this checkout.
 *
 * The safe repair and the one to try first: it reports what the provider says
 * and cannot manufacture a payment. A pending checkout is usually a consumer
 * who paid and never returned to the site, which is exactly what this
 * recovers — webhooks almost never arrive, so the return-to-site sync is what
 * normally marks a checkout paid.
 */
export function recheckRequestPayment(
  checkoutId: string,
): Promise<{ status: string; paidAt: string | null; changed: boolean }> {
  return apiClient
    .post<{ status: string; paidAt: string | null; changed: boolean }>(
      `/platform/request-payments/${checkoutId}/recheck`,
      {},
    )
    .then((res) => res.data);
}

/**
 * Refund a paid checkout in full. Partial refunds are not supported — the
 * underlying provider call refunds the whole payment.
 */
export function refundRequestPayment(checkoutId: string): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/platform/request-payments/${checkoutId}/refund`, {})
    .then((res) => res.data);
}

/**
 * Assert a payment the provider never confirmed.
 *
 * The reason is REQUIRED and is stored on the checkout row, not merely
 * logged: a spawned request and a professional payout follow from a paid row,
 * so the override has to be answerable later. Reach for recheck first — it
 * produces the same outcome when the money is real, and correctly refuses
 * when it is not.
 */
export function markRequestPaymentPaid(
  checkoutId: string,
  reason: string,
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/platform/request-payments/${checkoutId}/mark-paid`, { reason })
    .then((res) => res.data);
}
